import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X, Loader2, Maximize2, Minimize2, AlertTriangle, Wallet, Gamepad2,
  RotateCcw,
} from 'lucide-react';
import client, { API } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { getDemoId } from '../hooks/useGameLauncher';
import { useSettings } from '../context/SettingsContext';
import { launchErrorParts } from '../lib/launchError';
import {
  classifyFrameLoad, frameGuardEnabled, frameReturnedToSite,
  normalizeLobbyMode, LOBBY_SITE,
} from '../lib/gameFrameExit';
import { gamePageHook } from '../lib/domHooks';

const DEMO_ID_KEY = 'pusubet_demo_id';
const GAME_SESSION_KEY = 'pusubet_game_session_id';
// Sağlayıcı iframe'i bu süre içinde yüklenmezse kullanıcıya çıkış yolu sunulur.
// Oturum iptal edilmez, yalnızca alternatif gösterilir.
const SLOW_LOAD_MS = 9000;
// Sağlayıcı adresine erişilebiliyor mu kontrolü bu gecikmeyle yapılır.
const REACH_CHECK_MS = 4000;
const LAUNCH_TIMEOUT_MS = 30000;
// Oyun açıkken ana bakiyenin tazelenme aralığı. Kabuğun olağan turu 15
// saniyedir; oyunun içinde her spin cüzdanı değiştirdiği için burada
// kısaltılır.
const REAL_BALANCE_SYNC_MS = 5000;

const fullscreenSupported = () => (
  typeof document !== 'undefined'
  && !!(document.documentElement.requestFullscreen
        || document.documentElement.webkitRequestFullscreen)
);

/**
 * Oyunu iframe içinde açar.
 *
 * mode='real' + ScorpioPlay : transfer-wallet — açılışta bakiye sağlayıcıya
 *   aktarılır, kapanışta geri alınır.
 * mode='real' + Drakon      : seamless — cüzdan bizde kalır, aktarım yok.
 * mode='demo'               : demo cüzdanı, gerçek bakiye etkilenmez,
 *   giriş yapılmamış olsa bile oynanabilir.
 *
 * layout='page': oyunun kendi sayfasında (`/oyun/:id`, pages/GamePage.jsx)
 *   akışın içinde çizilir — karartma katmanı, gövde kaydırma kilidi ve ESC ile
 *   kapatma yoktur; araç şeridi oyunun **altındadır** ve sayfa ek düğmeler
 *   (mod anahtarı, sinema modu) verebilir (`toolbarExtra`). Launch, oturum
 *   kapatma, lobi kapısı ve hata ekranları pencereyle **aynı koddur**; iki
 *   kopya ayrışırsa bir oyun pencerede açılıp sayfada açılmazdı. Sayfadan
 *   başka bir adrese geçilince (öneri, menü) oturum ayrıca kapatılır: pencerede
 *   çıkışın tek yolu `close()` idi, sayfada ise bileşen sessizce sökülür.
 */
const GameModal = ({
  game, mode = 'real', onClose, layout = 'modal', toolbarExtra = null,
}) => {
  const pageLayout = layout === 'page';
  const { refresh, setBalance } = useAuth();
  // Vega'da oyun **kaplayarak** açılır: pencere kabuğu, küçültme ve kapatma
  // düğmesi çizilmez, oyuncu cihazının geri hareketiyle çıkar (aşağıdaki
  // geçmiş girişi). Referans arayüzün davranışı budur; diğer şemalarda
  // pencere ve düğmeleri olduğu gibi kalır.
  const settings = useSettings();
  const immersive = !pageLayout && settings.site_theme === 'vega';
  const { toast } = useToast();
  const navigate = useNavigate();
  const [state, setState] = useState('loading'); // loading | ready | error | closing
  const [url, setUrl] = useState('');
  // Gateway kullanılıyorsa oyunun kendi sekmesinde açılabileceği kabuk sayfası.
  // Bilet adresten çözülür ve yol her zaman göreli kalır: kabuk, oturumu
  // kapatabilmek için ana site alan adında açılmalıdır (oyunun kendisi ayrı bir
  // gateway alan adında olabilir).
  const standaloneUrl = React.useMemo(() => {
    const match = String(url || '').match(/\/gamelaunch\/([A-Za-z0-9_-]{40,64})\//);
    return match ? `/gamelaunch/${match[1]}/view/` : '';
  }, [url]);
  const [error, setError] = useState('');
  const errorParts = launchErrorParts(error);
  const [transferred, setTransferred] = useState(0);
  const [seamless, setSeamless] = useState(false);
  const [demoBalance, setDemoBalance] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  // iframe gerçekten yüklendi mi: launch yanıtı gelmiş olsa da sağlayıcı
  // sayfası açılmamış olabilir (mobilde en sık görülen "oyun açılmıyor" hâli).
  const [frameLoaded, setFrameLoaded] = useState(false);
  const [slow, setSlow] = useState(false);
  // Sağlayıcı adresine tarayıcıdan hiç ulaşılamıyor mu (kalkan/engelleyici,
  // DNS, ağ). null = henüz bilinmiyor.
  const [blocked, setBlocked] = useState(null);
  const launchedRef = useRef(false);
  const sessionIdRef = useRef('');
  const shellRef = useRef(null);
  // Sayfa düzeninde araç şeridi bir CSS tarafından gizlenmiş mi (Betrun'un
  // görünümü ve kurulumların özel CSS'i mobilde gizler). Gizliyse tam ekran
  // düğmesi oyun alanının köşesine taşınır — şeridi gizlemeyen sitede ikinci
  // bir düğme çizilmez.
  const toolbarRef = useRef(null);
  const [toolbarHidden, setToolbarHidden] = useState(false);
  const iframeRef = useRef(null);
  const canFullscreen = useRef(fullscreenSupported()).current;
  const isDemo = mode === 'demo';
  // Oyun içi lobi kapısı (bkz. lib/gameFrameExit.js): ilk yüklemenin ve son
  // yeniden açılışın zamanı. State değil ref'tir — yükleme olayı render
  // beklemeden karar vermelidir.
  const firstLoadAtRef = useRef(null);
  const lastRelaunchAtRef = useRef(null);
  // Oyun içi lobi düğmesine basıldığında ne olacağı panelden seçilir
  // (bkz. lib/gameFrameExit.js).
  const lobbyMode = normalizeLobbyMode(settings.game_lobby_mode);
  const guardFrame = frameGuardEnabled(game, mode, lobbyMode);

  const launch = useCallback(async function run(opts = {}) {
    firstLoadAtRef.current = null;
    setFrameLoaded(false);
    setSlow(false);
    setBlocked(null);
    try {
      if (isDemo) {
        // language gönderilmez: oyun dili yönetim panelindeki ayardan gelir.
        const { data } = await client.post('/games/demo-launch', {
          game_id: game.id, demo_id: getDemoId(),
        }, { timeout: LAUNCH_TIMEOUT_MS });
        if (data.demo_id) {
          try { localStorage.setItem(DEMO_ID_KEY, data.demo_id); } catch { /* yok sayılır */ }
        }
        setUrl(data.launch_url);
        setDemoBalance(Number(data.demo_balance || 0));
        setSeamless(true);
      } else {
        const { data } = await client.post('/games/launch', {
          game_id: game.id, demo: false,
        }, { timeout: LAUNCH_TIMEOUT_MS });
        sessionIdRef.current = data.session_id || '';
        if (sessionIdRef.current) {
          try {
            sessionStorage.setItem(GAME_SESSION_KEY, sessionIdRef.current);
          } catch { /* depolama erişimi yoksa ref yeterlidir */ }
        }
        setUrl(data.launch_url);
        const seamlessLaunch = !!data.seamless;
        setSeamless(seamlessLaunch);
        setTransferred(Number(data.transferred || 0));
        // Seamless sağlayıcıda launch hiçbir para hareketi üretmez ve yanıttaki
        // `balance` kabuğun yazdığı ana bakiye değil, sağlayıcıya bildirilen
        // **oynanabilir** toplamdır (ana + bonus). Kabuğa yazılırsa bonus ikinci
        // kez sayılır — altı kabuk da bonusu ayrı satırda gösterir — ve ardından
        // gelen /auth/me tazelemesi değeri ana bakiyeye geri çeker; oyuncu bunu
        // "oyuna girdim, bakiyem silindi" diye bildirir. Transfer-wallet'ta ise
        // bakiye gerçekten sağlayıcıya aktarılmıştır, kabuk güncellenmelidir.
        if (!seamlessLaunch && data.balance != null) setBalance(Number(data.balance));
      }
      setState('ready');
    } catch (e) {
      // Mobilde uygulama arka plana alınıp sekme kapatıldığında önceki oyun
      // oturumu kapanmadan kalabiliyor; sunucu 409 döndüğünde bir kez
      // kapatıp yeniden deniyoruz.
      if (e.response?.status === 409 && !opts.retried && !isDemo) {
        try {
          const { data } = await client.get('/games/session');
          if (data.session?.id) {
            await client.post('/games/close', {
              session_id: data.session.id,
            });
            try {
              if (sessionStorage.getItem(GAME_SESSION_KEY) === data.session.id) {
                sessionStorage.removeItem(GAME_SESSION_KEY);
              }
            } catch { /* yok sayılır */ }
          }
        } catch { /* yok sayılır */ }
        return run({ retried: true });
      }
      const timedOut = e.code === 'ECONNABORTED';
      setError(
        e.response?.data?.detail
        || (timedOut
          ? 'Sağlayıcıya bağlanılamadı, bağlantınızı kontrol edip tekrar deneyin'
          : 'Oyun başlatılamadı'),
      );
      setState('error');
    }
    return undefined;
  }, [game.id, isDemo, setBalance]);

  useEffect(() => {
    if (launchedRef.current) return;
    launchedRef.current = true;
    launch();
  }, [launch]);

  // Modal açıkken arka planın kaymaması için gövde kaydırması kilitlenir.
  // Mobilde iframe üzerindeki dokunuşlar aksi hâlde sayfayı sürüklüyor.
  // Sayfa düzeninde kilit yoktur: oyunun altındaki bilgi kartına ve sağdaki
  // önerilere kaydırılarak ulaşılır.
  useEffect(() => {
    if (pageLayout) return undefined;
    const { style } = document.body;
    const prevOverflow = style.overflow;
    const prevOverscroll = style.overscrollBehavior;
    document.body.classList.add('game-modal-open');
    window.LiveChatWidget?.call?.('hide');
    style.overflow = 'hidden';
    style.overscrollBehavior = 'none';
    return () => {
      document.body.classList.remove('game-modal-open');
      window.LiveChatWidget?.call?.('minimize');
      style.overflow = prevOverflow;
      style.overscrollBehavior = prevOverscroll;
    };
  }, [pageLayout]);

  // Sağlayıcı sayfası uzun süre açılmazsa kullanıcıya alternatif sunulur.
  useEffect(() => {
    if (state !== 'ready' || frameLoaded) return undefined;
    const t = setTimeout(() => setSlow(true), SLOW_LOAD_MS);
    return () => clearTimeout(t);
  }, [state, frameLoaded]);

  // iframe açılmıyorsa nedenini ayırt et: sağlayıcı adresine ``no-cors`` bir
  // istek atılır. Yanıt opak da olsa gelirse adres erişilebilir demektir
  // (yavaş yükleme); istek ağ hatasıyla düşerse tarayıcı tarafında bir engel
  // vardır (kalkan/reklam engelleyici, DNS, ağ kısıtı).
  useEffect(() => {
    if (state !== 'ready' || frameLoaded || !url) return undefined;
    let alive = true;
    const t = setTimeout(() => {
      let origin;
      try { origin = new URL(url, window.location.origin).origin; } catch { return; }
      fetch(`${origin}/favicon.ico`, { mode: 'no-cors', cache: 'no-store' })
        .then(() => { if (alive) setBlocked(false); })
        .catch(() => { if (alive) setBlocked(true); });
    }, REACH_CHECK_MS);
    return () => { alive = false; clearTimeout(t); };
  }, [state, frameLoaded, url]);

  // Sandbox içindeki gateway hata sayfası ana uygulamanın token/cookie
  // alanına erişemez; yalnızca bu sınırlı postMessage olayı ile nedeni bildirir.
  useEffect(() => {
    const onMessage = (event) => {
      if (
        event.source !== iframeRef.current?.contentWindow
        || event.data?.type !== 'game-gateway-error'
      ) return;
      setError(event.data.message || 'Oyun gateway bağlantısı kurulamadı');
      setState('error');
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const close = useCallback(async () => {
    if (isDemo || !sessionIdRef.current) {
      if (isDemo) await refresh().catch(() => {});
      onClose();
      return;
    }
    setState('closing');
    try {
      const { data } = await client.post('/games/close', {
        session_id: sessionIdRef.current,
      });
      if (data.returned > 0) {
        setBalance(Number(data.balance || 0));
        toast({
          title: 'Oyun kapatıldı',
          description: `${Number(data.returned).toFixed(2)} ₺ bakiyenize geri aktarıldı`,
        });
      }
    } catch (e) {
      toast({
        title: 'Bakiye geri alınamadı',
        description: e.response?.data?.detail || 'Lütfen müşteri hizmetleri ile iletişime geçin',
      });
    } finally {
      const closedSessionId = sessionIdRef.current;
      sessionIdRef.current = '';
      try {
        if (sessionStorage.getItem(GAME_SESSION_KEY) === closedSessionId) {
          sessionStorage.removeItem(GAME_SESSION_KEY);
        }
      } catch { /* yok sayılır */ }
      await refresh().catch(() => {});
      onClose();
    }
  }, [isDemo, onClose, refresh, setBalance, toast]);

  const resetDemo = useCallback(async () => {
    const id = getDemoId();
    if (!id) return;
    try {
      const { data } = await client.post(`/games/demo-reset/${id}`);
      setDemoBalance(Number(data.balance || 0));
      toast({ title: 'Demo bakiye yenilendi', description: `${Number(data.balance).toFixed(2)} ₺` });
    } catch {
      toast({ title: 'Demo bakiye yenilenemedi' });
    }
  }, [toast]);

  // Tam ekran: destekleyen cihazlarda gerçek tam ekran (mobilde adres çubuğu
  // da gizlenir), desteklemeyenlerde yalnızca modal genişletilir.
  const toggleFullscreen = useCallback(() => {
    const el = shellRef.current;
    if (!canFullscreen || !el) { setFullscreen((v) => !v); return; }
    if (document.fullscreenElement || document.webkitFullscreenElement) {
      (document.exitFullscreen || document.webkitExitFullscreen)?.call(document);
    } else {
      (el.requestFullscreen || el.webkitRequestFullscreen)?.call(el);
      setFullscreen(true);
    }
  }, [canFullscreen]);

  useEffect(() => {
    const onChange = () => setFullscreen(
      !!(document.fullscreenElement || document.webkitFullscreenElement),
    );
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
    };
  }, []);

  // Şeridin görünürlüğü ölçülür, sorulmaz: onu gizleyen kural bir şemadan
  // ya da kurulumun panelden yazdığı CSS'ten gelebilir ve o CSS ayarlarla
  // birlikte sonradan yüklenir. `display: none` öğenin boyutunu sıfırladığı
  // için ResizeObserver her geçişte haber verir; ekran döndürmede de.
  useEffect(() => {
    const el = toolbarRef.current;
    if (!pageLayout || !el) return undefined;
    const measure = () => setToolbarHidden(el.getClientRects().length === 0);
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [pageLayout]);

  // ESC ile kapatma. Sayfa düzeninde yoktur: ESC orada tam ekrandan çıkmanın
  // tuşudur ve oyuncuyu sayfadan atmamalıdır.
  useEffect(() => {
    if (pageLayout) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close, pageLayout]);

  // Sayfa düzeninde oyuncu kapat düğmesine basmadan başka bir adrese geçebilir
  // (öneri, menü, geri hareketi). Bileşen sökülürken açık oturum kapatılır;
  // `close()` üzerinden çıkıldıysa oturum numarası zaten boşaltılmıştır.
  useEffect(() => {
    if (!pageLayout) return undefined;
    return () => {
      const sessionId = sessionIdRef.current;
      if (!sessionId) return;
      sessionIdRef.current = '';
      try {
        if (sessionStorage.getItem(GAME_SESSION_KEY) === sessionId) {
          sessionStorage.removeItem(GAME_SESSION_KEY);
        }
      } catch { /* yok sayılır */ }
      client.post('/games/close', { session_id: sessionId })
        .then(({ data }) => {
          if (data?.returned > 0) setBalance(Number(data.balance || 0));
        })
        .catch(() => {});
    };
  }, [pageLayout, setBalance]);

  // Kapatma düğmesi çizilmeyen şemada çıkış yolu cihazın **geri** hareketidir.
  // Oyun bir rota değil bir penceredir, bu yüzden geçmişe bir giriş eklenir:
  // eklenmezse geri hareketi oyunu değil siteyi kapatır ve oyuncu turun
  // ortasında dışarı düşerdi. Efekt `close`'a bağlanmaz (o her bakiye
  // değişiminde yeniden kurulur ve her seferinde ikinci bir giriş eklenirdi);
  // güncel kapatma bir ref üzerinden okunur.
  const closeRef = useRef(close);
  useEffect(() => { closeRef.current = close; }, [close]);

  useEffect(() => {
    if (!immersive) return undefined;
    window.history.pushState({ bhsGame: true }, '');
    const onPop = () => closeRef.current();
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      // Pencere başka yolla kapandıysa (ESC, oturum hatası) eklenen giriş
      // geride kalmamalı: kalırsa oyuncunun bir sonraki geri hareketi hiçbir
      // şey yapmaz. Geri hareketiyle kapandıysa giriş zaten tüketilmiştir.
      if (window.history.state?.bhsGame) window.history.back();
    };
  }, [immersive]);

  // Gerçek para bakiyesi oyun açıkken **daha sık** tazelenir.
  //
  // Kabuğun olağan turu 15 saniyededir (bkz. context/AuthContext.jsx) ve
  // sıradan gezinmede fazlasıyla yeterlidir; oyunun içinde değildir: seamless
  // sağlayıcıda her spin cüzdanı anında değiştirir ve oyuncu iki ekranı yan
  // yana görür — oyunun kendi bakiyesi ile sitenin bakiyesi. Aradaki fark
  // "kazancım geç ekleniyor, kaybım geç düşüyor" diye bildiriliyordu.
  //
  // Tur yalnızca pencere **açıkken** ve sekme öndeyken çalışır; oyun
  // kapandığında `close()` zaten bir kez tazeler.
  useEffect(() => {
    if (isDemo || state !== 'ready') return undefined;
    const tick = () => {
      if (document.visibilityState === 'hidden') return;
      refresh().catch(() => {});
    };
    const timer = window.setInterval(tick, REAL_BALANCE_SYNC_MS);
    window.addEventListener('focus', tick);
    document.addEventListener('visibilitychange', tick);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', tick);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [isDemo, state, refresh]);

  // Demo bakiyesini periyodik güncelle (webhook ile değişiyor)
  useEffect(() => {
    if (!isDemo || state !== 'ready') return undefined;
    const id = getDemoId();
    if (!id) return undefined;
    const t = setInterval(() => {
      client.get(`/games/demo-balance/${id}`)
        .then(({ data }) => setDemoBalance(Number(data.balance || 0)))
        .catch(() => {});
    }, 10000);
    return () => clearInterval(t);
  }, [isDemo, state]);

  // Sekme kapanırsa transfer-wallet oturumunu kapatmayı dene (bakiye sağlayıcıda
  // kalmasın). iOS'ta beforeunload tetiklenmediği için pagehide de dinlenir;
  // bfcache'e giren sayfa (persisted) geri dönebileceğinden kapatılmaz.
  // sendBeacon Authorization başlığı gönderemediği için keepalive fetch kullanılıyor.
  useEffect(() => {
    if (isDemo || seamless) return undefined;
    const closeSession = () => {
      if (!sessionIdRef.current) return;
      const token = localStorage.getItem('pusubet_token');
      if (!token) return;
      fetch(`${API}/games/close`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ session_id: sessionIdRef.current }),
        keepalive: true,
      }).catch(() => {});
    };
    const onUnload = () => closeSession();
    const onPageHide = (e) => { if (!e.persisted) closeSession(); };
    window.addEventListener('beforeunload', onUnload);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('beforeunload', onUnload);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [isDemo, seamless]);

  const shellClass = pageLayout
    ? `game-page-player${fullscreen ? ' is-full' : ''}${isDemo ? ' is-demo' : ''}`
    : `game-modal-shell relative w-full h-full overflow-hidden flex flex-col${immersive ? '' : ' md:rounded-2xl'}`;
  const shellStyle = pageLayout ? undefined : {
    maxWidth: fullscreen || immersive ? '100%' : 1280,
    maxHeight: fullscreen || immersive ? '100%' : '92vh',
    background: 'var(--pusu-bg)',
    border: `1px solid ${isDemo ? '#3b82f6' : 'var(--pusu-border)'}`,
  };

  const bar = (
        <div ref={pageLayout ? toolbarRef : undefined}
             className={pageLayout
          ? 'game-page-toolbar'
          : 'game-modal-bar flex items-center gap-2 sm:gap-3 px-3 sm:px-4 h-12 sm:h-14 shrink-0'}
             {...(pageLayout ? gamePageHook('toolbar') : {})}
             style={pageLayout ? undefined : { background: 'var(--pusu-bg-2)', borderBottom: '1px solid var(--pusu-border)' }}>
          {/* Sayfa düzeninde oyunun adı altındaki bilgi kartındadır; şerit
              yalnızca düğmeleri taşır (DEMO rozeti kalır). */}
          {pageLayout ? (
            <div className="min-w-0 flex-1">
              {isDemo && (
                <span className="px-2 py-0.5 rounded text-[10px] font-bold"
                      style={{ background: '#3b82f6', color: '#fff' }}>DEMO</span>
              )}
            </div>
          ) : (
          <div className="min-w-0 flex-1">
            <div className="text-[13px] sm:text-sm font-bold text-white truncate flex items-center gap-2">
              {game.name}
              {isDemo && (
                <span className="px-2 py-0.5 rounded text-[10px] font-bold shrink-0"
                      style={{ background: '#3b82f6', color: '#fff' }}>DEMO</span>
              )}
            </div>
            <div className="text-[10px] sm:text-[11px] text-gray-500 truncate">{game.provider}</div>
          </div>
          )}

          {isDemo && demoBalance !== null && (
            <>
              <div className="flex items-center gap-1.5 px-2 sm:px-2.5 h-8 rounded-lg text-[11px] sm:text-xs shrink-0"
                   style={{ background: 'var(--pusu-card-2)', border: '1px solid #2a4a7a' }}>
                <Gamepad2 size={12} className="text-blue-400" />
                <span className="text-gray-400 hidden sm:inline">Demo:</span>
                <span className="font-bold text-blue-400">{demoBalance.toFixed(2)} ₺</span>
              </div>
              <button onClick={resetDemo} className="side-icon-btn game-modal-btn inline-flex"
                      title="Demo bakiyeyi yenile" aria-label="Demo bakiyeyi yenile">
                <RotateCcw size={15} />
              </button>
            </>
          )}

          {/* Sağlayıcı açılış bağlantısı tek kullanımlıktır; mobilde sayfa
              arka plandan dönünce iframe kendini yenilerse bağlantı geçersiz
              kalıyor. Bu düğme yeni bir oturum bağlantısı alır. */}
          {!isDemo && state === 'ready' && (
            <button onClick={() => { setState('loading'); launch(); }}
                    className="side-icon-btn game-modal-btn inline-flex"
                    title="Oyunu yeniden yükle" aria-label="Oyunu yeniden yükle">
              <RotateCcw size={15} />
            </button>
          )}

          {toolbarExtra}

          {!isDemo && transferred > 0 && (
            <div className="hidden sm:flex items-center gap-1.5 px-2.5 h-8 rounded-lg text-xs"
                 style={{ background: 'var(--pusu-card-2)', border: '1px solid var(--pusu-border)' }}>
              <Wallet size={12} className="text-[var(--pusu-gold)]" />
              <span className="text-gray-400">Oyunda:</span>
              <span className="gold-text font-bold">{transferred.toFixed(2)} ₺</span>
            </div>
          )}

          {/* Kaplayan şemada ikisi de çizilmez: pencere zaten ekranı kaplar
              ve çıkış cihazın geri hareketidir (ESC masaüstünde çalışmaya
              devam eder). */}
          {!immersive && (
            <>
              <button onClick={toggleFullscreen}
                      className="side-icon-btn game-modal-btn inline-flex"
                      title="Tam ekran" aria-label="Tam ekran">
                {fullscreen ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
              </button>
              <button onClick={close} className="side-icon-btn game-modal-btn inline-flex"
                      title={pageLayout ? 'Oyunu kapat' : 'Kapat (ESC)'} aria-label="Oyunu kapat">
                <X size={16} />
              </button>
            </>
          )}
        </div>
  );

  const content = (
      <div
        ref={shellRef}
        className={shellClass}
        style={shellStyle}
        {...(pageLayout ? gamePageHook('player') : {})}
      >
        {/* Pencerede araç şeridi üsttedir; sayfada oyunun altında durur. */}
        {!pageLayout && bar}

        {/* Gövde */}
        <div
          className="game-modal-stage relative flex-1 min-w-0 min-h-0 bg-black overflow-hidden"
          {...(pageLayout ? gamePageHook('stage') : {})}
        >
          {/* Oyun alanının köşesindeki tam ekran düğmesi: yalnızca araç şeridi
              gizliyken çizilir — o zaman tam ekrana geçmenin ve tam ekrandan
              çıkmanın tek yolu budur. Sahnenin içindedir ki tam ekranda da
              görünsün. */}
          {pageLayout && toolbarHidden && (
            <button
              type="button"
              onClick={toggleFullscreen}
              className="game-stage-fullscreen"
              title={fullscreen ? 'Tam ekrandan çık' : 'Tam ekran'}
              aria-label={fullscreen ? 'Tam ekrandan çık' : 'Tam ekran'}
              {...gamePageHook('fullscreen')}
            >
              {fullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </button>
          )}
          {/* Yalnızca launch isteği sürerken tam ekran örtü gösterilir. iframe
              yerleştikten sonra sağlayıcının kendi yükleme ekranı görünmelidir;
              üzerine opak bir katman koymak oyunu açılmamış gibi gösteriyordu. */}
          {state === 'loading' && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 px-6 text-center text-gray-400"
                 style={{ background: '#000' }}>
              <Loader2 size={32} className="animate-spin text-[var(--pusu-gold)]" />
              <div className="text-sm">
                {isDemo ? 'Demo oyun yükleniyor...' : 'Oyun yükleniyor, bakiyeniz hazırlanıyor...'}
              </div>
            </div>
          )}

          {/* Sağlayıcı sayfası açılmazsa oyunun üstünü kapatmayan bir şerit ile
              nedeni ve çıkış yolu gösterilir. Engel tespit edildiyse metin
              tarayıcı kalkanını işaret eder; aksi hâlde yavaş yükleme uyarısı. */}
          {state === 'ready' && !frameLoaded && (slow || blocked) && (
            <div className="absolute inset-x-0 bottom-0 z-10 px-3 py-3"
                 style={{ background: 'rgba(8,6,13,0.96)', borderTop: '1px solid var(--pusu-border)' }}>
              <div className="flex items-start gap-2 mb-2.5">
                <AlertTriangle size={15} className={`mt-0.5 shrink-0 ${blocked ? 'text-red-400' : 'text-yellow-400'}`} />
                <div className="text-xs text-gray-300 leading-relaxed">
                  {blocked ? (
                    <>
                      <span className="font-semibold text-white">Tarayıcınız oyun sağlayıcısını engelliyor.</span>{' '}
                      Brave Shields / reklam engelleyiciyi bu site için kapatıp
                      oyunu yeniden yükleyin.
                    </>
                  ) : (
                    <>
                      <span className="font-semibold text-white">Oyun bu pencerede görüntülenemiyor.</span>{' '}
                      Bağlantınız yavaşsa yeniden yükleme işe yarayabilir.
                    </>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => { setState('loading'); launch(); }}
                        className="gold-btn px-3 py-2 rounded-lg text-xs">
                  Yeniden Yükle
                </button>
                {/* Bazı oyunlar iç içe çerçevede açılmıyor. Kabuk sayfası oyunu
                    kendi sekmesinde açar; oturum kapatılmadan modal kapanır ki
                    oyuncu diğer sekmede kaldığı yerden devam edebilsin. */}
                {standaloneUrl && (
                  <a
                    href={standaloneUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => { setTimeout(onClose, 0); }}
                    className="gold-outline-btn px-3 py-2 rounded-lg text-xs"
                  >
                    Ayrı Sekmede Aç
                  </a>
                )}
              </div>
            </div>
          )}

          {state === 'closing' && (
            <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 text-gray-300"
                 style={{ background: 'rgba(0,0,0,0.85)' }}>
              <Loader2 size={32} className="animate-spin text-[var(--pusu-gold)]" />
              <div className="text-sm">Oyun kapatılıyor, bakiye geri alınıyor...</div>
            </div>
          )}

          {state === 'error' && (
            <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-4 p-6 text-center">
              <AlertTriangle size={36} className="text-red-400" />
              <div>
                <div className="font-semibold text-white mb-1">{errorParts.title}</div>
                <div className="text-sm text-gray-400 max-w-md">{errorParts.text}</div>
              </div>
              <div className="flex flex-wrap justify-center gap-3">
                <button onClick={() => { setState('loading'); launch(); }}
                        className="gold-outline-btn px-5 py-2.5 rounded-xl text-sm">
                  Tekrar Dene
                </button>
                <button onClick={onClose} className="gold-btn px-5 py-2.5 rounded-xl text-sm">Kapat</button>
              </div>
            </div>
          )}

          {/* allow-same-origin: oyunlar localStorage/postMessage olmadan
              açılmıyor, opaque origin'de sağlayıcı yükleyicileri duruyor.
              Gateway adresi kullanıldığında bu, sağlayıcıyı ana site ile aynı
              origin'e sokar — gateway kabuğunun izolasyonuna güvenilir. */}
          {url && (state === 'ready' || state === 'closing') && (
            <iframe
              ref={iframeRef}
              src={url}
              title={game.name}
              className="game-modal-frame"
              width="100%"
              height="100%"
              scrolling="no"
              allow="autoplay; fullscreen; payment; accelerometer; gyroscope; clipboard-write; encrypted-media"
              sandbox="allow-scripts allow-forms allow-popups allow-modals allow-same-origin allow-pointer-lock allow-presentation allow-downloads"
              allowFullScreen
              onLoad={() => {
                // Oyun içindeki "ana sayfa/lobi" düğmesi çerçeveyi sitenin
                // dönüş adresine götürdüyse oyun bitmiştir: pencerede sitenin
                // kendisini çizmek yerine oyun kapatılır (bkz.
                // lib/gameFrameExit.js).
                if (frameReturnedToSite(iframeRef.current)) {
                  close();
                  return;
                }
                if (guardFrame) {
                  const now = Date.now();
                  const kind = classifyFrameLoad({
                    now,
                    firstLoadAt: firstLoadAtRef.current,
                    lastRelaunchAt: lastRelaunchAtRef.current,
                  });
                  if (kind === 'first') firstLoadAtRef.current = now;
                  if (kind === 'loop') {
                    // Yeniden açılan oyun da hemen sayfa değiştirdi: sağlayıcı
                    // kendini yeniliyor olabilir. Sonsuz launch yerine kapı
                    // susar ve çerçeve **olduğu gibi kalır** — burada pencereyi
                    // kapatmak, kapı yanlış tetiklendiğinde oyuncuyu turun
                    // ortasında dışarı atıyordu ("oyundan atıyor"). Kapalı
                    // oyuna gelen bahsi zaten cüzdan webhook'u reddeder.
                    setFrameLoaded(true);
                    return;
                  }
                  if (kind === 'navigated') {
                    lastRelaunchAtRef.current = now;
                    if (lobbyMode === LOBBY_SITE) {
                      // Panelden seçilen davranış: lobi düğmesi oyuncuyu
                      // **sitenin kendi** oyun listesine götürür. Oturum
                      // kapatılır (seamless olmayan sağlayıcıda bakiye
                      // sağlayıcıda kalırdı), sonra vitrine geçilir.
                      close().finally(() => navigate('/slot'));
                      return;
                    }
                    // Çerçeve oyundan sağlayıcının lobisine geçti: lobi
                    // gösterilmez, aynı oyun taze bir açılışla geri gelir.
                    toast({
                      tone: 'warning',
                      title: 'Oyun içi lobi kullanılamaz',
                      description: 'Oyuna geri döndünüz. Başka bir oyun için sitemizdeki listeden seçin.',
                    });
                    setUrl('');
                    setState('loading');
                    launch();
                    return;
                  }
                }
                setFrameLoaded(true);
              }}
            />
          )}
        </div>
        {pageLayout && bar}
      </div>
  );

  if (pageLayout) return content;

  return (
    <div className="game-modal-root fixed inset-0 z-[200] flex items-center justify-center p-0 md:p-4">
      <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.92)' }} />
      {content}
    </div>
  );
};

export default GameModal;
